import { Component } from "vue-property-decorator";
import { API_ENDPOINT } from "@/constants";
import { AutomaticReport } from "@/models";
import {
  GranularityKind,
  WeekType,
  TimeSlot,
} from "@/components/Agencies/ReportBuilder/automaticReport";
import { CompetitorReportTemplateParameters } from "@/mixins/http/ReportTemplateMixin";
import Base from "./Base";

const HTTP_REQUEST_ACTION = "http/request";

export enum AutomaticReportsTags {
  INDEX = "get_automatic_reports",
  CREATE = "create_automatic_reports",
  SHOW = "show_automatic_reports",
  UPDATE = "update_automatic_reports",
  DESTROY = "destroy_automatic_reports",
  BULK_DESTROY = "bulk_destroy_automatic_reports",
  TOGGLE_ACTIVE = "toggle_active_automatic_reports",
}

// The reports index REQUIRES a kind (400 without it); only `competitor` is live.
export type IndexAutomaticReportParameters = {
  kind: "competitor";
};

// Nested competitor sub-settings NOT inferred from the recurrence: mirrors the
// report-template `competitor_attributes` structure. Keys are snake_case to match
// the backend strong params (Rails ParamsWrapper wraps the payload under
// `automatic_report`).
export type AutomaticReportCompetitorParameters = {
  id?: number;
  compare: boolean;
  comparison_starts_at?: string | null;
  comparison_ends_at?: string | null;
  time_slots: boolean;
  time_slots_filter?: TimeSlot[] | null;
};

export type AutomaticReportCommonParameters = {
  name: string;
  kind: "competitor";
  active: boolean;
  granularity_kind: GranularityKind;
  granularity_quantity?: number | null;
  granularity_range?: number[] | null;
  week_type?: WeekType | null;
  competitor_attributes: AutomaticReportCompetitorParameters;
};

export type AutomaticReportWithTemplateParameters =
  AutomaticReportCommonParameters & {
    report_template_id: number;
    template_competitor_attributes?: never;
    save_as_public_template?: never;
    public_template_name?: never;
  };

export type AutomaticReportWithInlineTemplateParameters =
  AutomaticReportCommonParameters & {
    template_competitor_attributes: CompetitorReportTemplateParameters;
    save_as_public_template: boolean;
    public_template_name?: string;
    report_template_id?: never;
  };

export type AutomaticReportParameters =
  | AutomaticReportWithTemplateParameters
  | AutomaticReportWithInlineTemplateParameters;

@Component
export default class AutomaticReportMixin extends Base {
  indexAutomaticReports(
    profileSlug: string,
    params: IndexAutomaticReportParameters
  ): Promise<AutomaticReport[]> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.INDEX,
      url,
      method: "GET",
      params: { kind: params.kind },
    });
  }

  destroyAutomaticReport(
    profileSlug: string,
    automaticReportId: string
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports/${automaticReportId}.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.DESTROY,
      url,
      method: "DELETE",
    });
  }

  bulkDestroyAutomaticReports(
    profileSlug: string,
    automaticReportIds: string[]
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports/bulk_destroy.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.BULK_DESTROY,
      url,
      method: "DELETE",
      data: { ids: automaticReportIds },
    });
  }

  createAutomaticReport(
    profileSlug: string,
    params: AutomaticReportParameters
  ): Promise<AutomaticReport> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.CREATE,
      url,
      method: "POST",
      data: { ...params },
    });
  }

  showAutomaticReport(
    profileSlug: string,
    automaticReportId: string
  ): Promise<AutomaticReport> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports/${automaticReportId}.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.SHOW,
      url,
      method: "GET",
    });
  }

  updateAutomaticReport(
    profileSlug: string,
    automaticReportId: string,
    params: AutomaticReportParameters
  ): Promise<AutomaticReport> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports/${automaticReportId}.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.UPDATE,
      url,
      method: "PATCH",
      data: { ...params },
    });
  }

  toggleActiveAutomaticReport(
    profileSlug: string,
    automaticReportId: string
  ): Promise<AutomaticReport> {
    const url = `${API_ENDPOINT}/${profileSlug}/automatic_reports/${automaticReportId}/toggle_active.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: AutomaticReportsTags.TOGGLE_ACTIVE,
      url,
      method: "PATCH",
    });
  }
}
